<template>
    <grid
        ref="charges"
        :is-auto-height="true"
        :is-auto-width="true"
        :is-search="false"
        :get-row-id="getRowId"
        :row-data="value"
        :column-defs="columnDefs"
        :style="{ marginBottom: '1rem' }"
        :pinnedBottomRowData="totals"
        :getRowStyle="getRowStyle"
        grid-theme="ag-theme-alpine-compact"
        :enableCellTextSelection="false"
        @on-row-data-updated="onRowDataUpdated"
    ></grid>
</template>

<script>
    import format from 'date-fns/format';
    import parse from 'date-fns/parse';
    import isEmpty from 'lodash/isEmpty';
    import isNil from 'lodash/isNil';
    import API from '../../../../api';
    import FormInput from '../../../generic/form-input/form-input.vue';
    import Grid from '../../../generic/grid/grid.vue';
    import ChargesActionCellRenderer from './charges-table-action-cell-renderer.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import { formatResponseError } from '../../../../utils/api';
    import { mapGetters } from 'vuex';
    import { toAmountDisplay } from '../../../../utils/numbers';

    const SERVER_DATE_FORMAT = 'yyyy-MM-dd';

    export default {
        name: 'ChargesTable',
        components: {
            Grid,
            Modal,
            chargesActionCellRenderer: ChargesActionCellRenderer,
        },
        inject: ['changeRateConfirm', 'changeCancellationFee', 'addRemoveDay'],
        props: {
            value: Array,
            totals: Array,
            metaReservationId: Number,
            bookingMethod: String,
        },
        computed: {
            ...mapGetters('header', ['userCurrencySymbol']),
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('header', ['userUnicodeChargesTableDateFormat']),
        },
        data() {
            return {
                isEdit: false,
                columnDefs: [
                    {
                        headerName: '',
                        minWidth: 80,
                        maxWidth: 80,
                        sortable: false,
                        resizable: false,
                        cellStyle: { 'align-items': 'flex-end' },
                        cellRenderer: 'chargesActionCellRenderer',
                        cellRendererParams: ({ data }) => {
                            return {
                                bookingMethod: this.bookingMethod,
                                isCancelled: data.is_cancelled,
                                addRemoveDay: this.addRemoveDay,
                            };
                        },
                    },
                    {
                        type: 'dateEditableColumn',
                        headerName: 'Date',
                        sortable: false,
                        field: 'date',
                        resizable: false,
                        editable: ({ data }) => data.type === 'FEE',
                        cellClass: ({ data }) => (data.type === 'FEE' ? 'text-primary lx-editable-cell' : ''),
                        flex: 2,
                        cellEditorParams: ({ data }) => ({
                            dateFormat: this.userUnicodeChargesTableDateFormat,
                        }),
                        //cellRenderer: ({ data, value }) => {
                        //    const hasWeekday = this.userUnicodeDateFormat.indexOf('EEE') == 0;
                        //    return (data.type == 'DAY' && (data.is_start_date || data.is_end_date)  && !hasWeekday && data.duration == 1 && window.innerWidth >= 768)
                        //        ? `${value}, ${data.weekday}`
                        //        : `${value ? value : ''}`;
                        //},
                        valueSetter: async ({ node, data, newValue }) => {
                            const oldDate = data.date;
                            data.date = newValue;

                            const date = format(
                                parse(newValue, this.userUnicodeChargesTableDateFormat, new Date()),
                                SERVER_DATE_FORMAT
                            );

                            (async () => {
                                try {
                                    await API.patch(`/app/reservation/invoice/${data.meta_res_id}/set-fee-date/`, {
                                        pk: data.id,
                                        date: date,
                                    });
                                    this.successToast({ text: `Fee date successfuly changed` });
                                } catch (err) {
                                    this.errorToast({ text: `Could not change fee date` });
                                    data.date = oldDate;
                                    node.setData(data);
                                }
                            })();

                            return true;
                        },
                    },
                    {
                        headerName: 'Description',
                        sortable: false,
                        resizable: false,
                        field: 'description',
                        flex: 5,
                        minWidth: 200,
                        autoWidthExclude: true,
                    },
                    {
                        type: 'editableColumn',
                        headerName: 'Amount',
                        field: 'amount',
                        minWidth: 130,
                        cellRenderer: ({ data, value }) => {
                            if (!data.is_cancelled) {
                                return toAmountDisplay({
                                    amount: value,
                                    currency: this.userCurrencySymbol,
                                });
                            } else {
                                return '-';
                            }
                        },
                        sortable: false,
                        resizable: false,
                        cellEditorParams: { isDecimal: true, select: true, stopEditingOnBlur: true, },
                        editable: ({ data }) => ['DAY', 'CANCELLATION_FEE'].includes(data.type),
                        cellClass: ({ data }) =>
                            ['DAY', 'CANCELLATION_FEE'].includes(data.type) ? 'text-primary lx-editable-cell' : '',
                        valueSetter: ({ node, data, newValue }) => {
                            if (data.type == 'DAY') {
                                this.changeRateConfirm(
                                    data.tech_res_id,
                                    data.sysdate,
                                    newValue,
                                    data.amount,
                                    data.duration
                                );
                            } else if (data.type == 'CANCELLATION_FEE') {
                                data.amount = newValue;
                                this.changeCancellationFee(data.id, newValue);
                            }
                        },
                    },
                    {
                        headerName: 'Discount',
                        sortable: false,
                        field: 'discount',
                        resizable: false,
                        flex: 1,
                        hide: true,
                        cellRenderer: ({ data }) => (data.is_cancelled ? '-' : data.discount),
                    },
                    {
                        headerName: 'Net',
                        sortable: false,
                        field: 'net',
                        resizable: false,
                        flex: 1,
                        hide: true,
                        cellRenderer: ({ data }) => (data.is_cancelled ? '-' : data.net),
                    },
                    {
                        headerName: 'Tax',
                        sortable: false,
                        field: 'tax',
                        resizable: false,
                        flex: 1,
                        cellRenderer: ({ data }) => (data.is_cancelled ? '-' : data.tax),
                    },
                    {
                        headerName: 'Total',
                        sortable: false,
                        field: 'total',
                        resizable: false,
                        flex: 1,
                        cellClass: 'ag-right-aligned-cell',
                        headerClass: 'ag-right-aligned-header',
                        cellRenderer: ({ data }) => (data.is_cancelled ? '-' : data.total),
                    },
                ],
            };
        },
        methods: {
            getRowId(item) {
                return item.data.pos?.toString();
            },
            getRowStyle(params) {
                if (params.node.rowPinned === 'bottom') {
                    return {
                        'font-weight': 'bold',
                        'background-color': '#F8F8F8',
                    };
                }
            },
            onRowDataUpdated({ api }) {
                if (this.totals[0].has_discount) {
                    api.setColumnsVisible(['discount', 'net'], true);
                } else {
                    api.setColumnsVisible(['discount', 'net'], false);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.ag-root-wrapper) {
        overflow: visible;
    }
</style>
